import type { Agendamento, EventoHistorico, Instalacao, Novidade } from '@/data/types';

/**
 * Massa de demonstração do PAN.
 *
 * Derivada do cenário usado no app original (uma controladora "Orion" com três
 * controladas), reescrita para cobrir explicitamente os casos que a interface
 * precisa saber desenhar: instalação sem atualização pendente, instalação sem
 * permissão de agendar, instalação em incidente, contrato suspenso e campo sem
 * fonte integrada. Nada de dado genérico — Hard rule 7.
 */

export const instalacoes: Instalacao[] = [
  {
    id: 'ins-ORION-CTRL',
    codigo: 'orion-sp',
    nome: 'Orion · Controladora (SP)',
    clienteId: 'cli-ORION',
    cliente: {
      id: 'cli-ORION',
      nome: 'Orion Participações',
      contrato: {
        id: 'cntr-ORION',
        status: 'Ativo',
        dataContratacao: '2024-03-18',
        modulos: [
          { id: 'mod-orion-1', nome: 'Fiscal', inicio: '2024-03-18', saude: 'Alinhado' },
          { id: 'mod-orion-2', nome: 'Tesouraria e Finanças', inicio: '2024-04-01', saude: 'Início diferenciado' },
          { id: 'mod-orion-3', nome: 'Contabilidade', inicio: '2024-03-18', saude: 'Alinhado' },
          { id: 'mod-orion-4', nome: 'Departamento de Pessoal', inicio: '2024-06-01', saude: 'Início diferenciado' },
        ],
      },
    },
    tipo: 'Cloud',
    regiao: 'sa-east-1',
    versaoAtual: '2025.11.2',
    versaoEstavelRecomendada: '2025.12.1',
    ultimaAtualizacao: '2025-12-12T09:15:00',
    saude: 'OK',
    papel: 'Controladora',
    administrador: true,
    isControladora: true,
    controladoraId: null,
    cloudCode: 'orion-sp',
    atualizar: true,
    ana: true,
    interno: false,
    escopo: {
      total: '4 instalações',
      texto: 'A instalação de acesso é Controladora: o escopo contém a Controladora e 3 controladas.',
      saude: '1 incidente',
      saudeDescricao: '1 instalação em atenção e 1 em incidente.',
      modo: 'Bulk (via Controladora)',
    },
  },
  {
    id: 'ins-DFM-PROD',
    codigo: 'dfm-rj',
    nome: 'DF&M · Produção (RJ)',
    clienteId: 'cli-DFM',
    cliente: {
      id: 'cli-DFM',
      nome: 'DF&M Alimentos',
      contrato: {
        id: 'cntr-DFM',
        status: 'Ativo',
        dataContratacao: '2023-11-07',
        modulos: [
          { id: 'mod-dfm-1', nome: 'Materiais', inicio: '2023-11-07', saude: 'Alinhado' },
          { id: 'mod-dfm-2', nome: 'Suprimentos', inicio: '2024-02-15', saude: 'Início diferenciado' },
          { id: 'mod-dfm-3', nome: 'Fiscal', inicio: '2023-11-07', saude: 'Alinhado' },
        ],
      },
    },
    tipo: 'On-premises',
    regiao: null,
    versaoAtual: '2025.10.5',
    versaoEstavelRecomendada: '2025.12.1',
    ultimaAtualizacao: '2025-11-28T22:40:00',
    saude: 'Atencao',
    papel: 'Controlada',
    administrador: false,
    isControladora: false,
    controladoraId: 'ins-ORION-CTRL',
    cloudCode: null,
    atualizar: true,
    ana: false,
    interno: false,
    escopo: { total: null, texto: null, saude: null, saudeDescricao: null, modo: 'Single-installation' },
  },
  {
    id: 'ins-ACME-HOMOLOG',
    codigo: 'acme-mg',
    nome: 'ACME · Homologação (MG)',
    clienteId: 'cli-ACME',
    cliente: {
      id: 'cli-ACME',
      nome: 'ACME Serviços e Comércio',
      contrato: {
        id: 'cntr-ACME',
        status: 'Suspenso',
        dataContratacao: '2022-08-30',
        modulos: [
          { id: 'mod-acme-1', nome: 'Recursos Humanos', inicio: '2022-08-30', saude: 'Alinhado' },
          { id: 'mod-acme-2', nome: 'Departamento de Pessoal', inicio: '2023-01-10', saude: 'Início diferenciado' },
        ],
      },
    },
    tipo: 'Cloud',
    regiao: 'sa-east-1',
    versaoAtual: '2025.12.1',
    versaoEstavelRecomendada: '2025.12.1',
    ultimaAtualizacao: '2026-01-06T03:20:00',
    saude: 'OK',
    papel: 'Controlada',
    administrador: true,
    isControladora: false,
    controladoraId: 'ins-ORION-CTRL',
    cloudCode: 'acme-mg',
    // Já está na versão mais recente: o fluxo de agendar precisa dizer isso, não
    // oferecer um formulário vazio.
    atualizar: false,
    ana: false,
    interno: true,
    escopo: { total: null, texto: null, saude: null, saudeDescricao: null, modo: 'Single-installation' },
  },
  {
    id: 'ins-VENTO-PROD',
    codigo: 'vento-pe',
    nome: 'Vento Norte · Produção (PE)',
    clienteId: 'cli-VENTO',
    cliente: {
      id: 'cli-VENTO',
      nome: 'Vento Norte Energia',
      contrato: {
        id: 'cntr-VENTO',
        status: 'Ativo',
        dataContratacao: '2025-02-03',
        modulos: [
          { id: 'mod-vento-1', nome: 'Contabilidade', inicio: '2025-02-03', saude: 'Alinhado' },
          { id: 'mod-vento-2', nome: 'Fiscal', inicio: '2025-05-19', saude: 'Início diferenciado' },
          { id: 'mod-vento-3', nome: 'Tesouraria e Finanças', inicio: '2025-02-03', saude: 'Alinhado' },
        ],
      },
    },
    tipo: 'Cloud',
    regiao: 'sa-east-1',
    versaoAtual: '2025.09.4',
    versaoEstavelRecomendada: '2025.12.1',
    ultimaAtualizacao: '2025-10-02T01:05:00',
    saude: 'Incidente',
    papel: 'Controlada',
    administrador: true,
    isControladora: false,
    controladoraId: 'ins-ORION-CTRL',
    cloudCode: 'vento-pe',
    atualizar: true,
    ana: false,
    interno: false,
    escopo: { total: null, texto: null, saude: null, saudeDescricao: null, modo: 'Single-installation' },
  },
];

export const agendamentos: Agendamento[] = [
  {
    id: 'sch-30011',
    instalacaoId: 'ins-ORION-CTRL',
    instalacao: 'Orion · Controladora (SP)',
    versao: '2025.12.1',
    data: '2026-02-13',
    hora: '11:00',
    status: 'Programado',
    origem: 'Direto',
    batchId: null,
    createdAt: '2025-12-25T09:10:00',
  },
  {
    id: 'sch-30012',
    instalacaoId: 'ins-VENTO-PROD',
    instalacao: 'Vento Norte · Produção (PE)',
    versao: '2025.12.0',
    data: '2026-02-13',
    hora: '11:00',
    status: 'Programado',
    origem: 'Lote via Controladora',
    batchId: 'lote-8841',
    createdAt: '2025-12-25T09:10:00',
  },
  {
    id: 'sch-30013',
    instalacaoId: 'ins-DFM-PROD',
    instalacao: 'DF&M · Produção (RJ)',
    versao: '2025.12.0',
    data: '2026-02-20',
    hora: '07:00',
    status: 'Cancelado',
    origem: 'Lote via Controladora',
    batchId: 'lote-8841',
    createdAt: '2025-12-25T09:10:00',
  },
  {
    id: 'sch-30014',
    instalacaoId: 'ins-ACME-HOMOLOG',
    instalacao: 'ACME · Homologação (MG)',
    versao: '2025.12.1',
    data: '2026-03-04',
    hora: '15:00',
    status: 'Programado',
    origem: 'Direto',
    batchId: null,
    createdAt: '2026-01-08T14:22:00',
  },
  // Já finalizados — mostram, em "Meus Agendamentos", que o mesmo status do
  // agendamento cobre o ciclo inteiro (não só Programado/Executando/Cancelado).
  {
    id: 'sch-30015',
    instalacaoId: 'ins-ACME-HOMOLOG',
    instalacao: 'ACME · Homologação (MG)',
    versao: '2025.11.2',
    data: '2026-01-15',
    hora: '08:00',
    status: 'Concluído',
    origem: 'Direto',
    batchId: null,
    createdAt: '2026-01-10T10:00:00',
  },
  {
    id: 'sch-30016',
    instalacaoId: 'ins-VENTO-PROD',
    instalacao: 'Vento Norte · Produção (PE)',
    versao: '2025.11.2',
    data: '2026-01-20',
    hora: '09:00',
    status: 'Falha',
    origem: 'Direto',
    batchId: null,
    createdAt: '2026-01-18T11:00:00',
  },
  {
    id: 'sch-30017',
    instalacaoId: 'ins-DFM-PROD',
    instalacao: 'DF&M · Produção (RJ)',
    versao: '2025.11.2',
    data: '2026-01-22',
    hora: '10:00',
    status: 'Falha Parcial',
    origem: 'Direto',
    batchId: null,
    createdAt: '2026-01-19T12:00:00',
  },
];

export const historico: EventoHistorico[] = [
  {
    id: 'upd-41803',
    instalacao: 'Orion · Controladora (SP)',
    versao: '2025.11.2',
    data: '2025-12-12T09:00:00',
    status: 'Concluído',
    origem: 'Direto',
    batchId: null,
  },
  {
    id: 'upd-41804',
    instalacao: 'ACME · Homologação (MG)',
    versao: '2025.12.1',
    data: '2026-01-06T03:20:00',
    status: 'Concluído',
    origem: 'Lote via Controladora',
    batchId: 'lote-8712',
  },
  {
    id: 'upd-41805',
    instalacao: 'Vento Norte · Produção (PE)',
    versao: '2025.10.1',
    data: '2025-10-02T01:05:00',
    status: 'Falha',
    origem: 'Lote via Controladora',
    batchId: 'lote-8712',
  },
  {
    id: 'upd-41806',
    instalacao: 'DF&M · Produção (RJ)',
    versao: '2025.12.0',
    data: '2026-02-20T07:00:00',
    status: 'Cancelado',
    origem: 'Lote via Controladora',
    batchId: 'lote-8841',
  },
  {
    id: 'upd-41807',
    instalacao: 'DF&M · Produção (RJ)',
    versao: '2025.10.5',
    data: '2025-11-28T22:40:00',
    status: 'Concluído',
    origem: 'Direto',
    batchId: null,
  },
  {
    id: 'upd-41808',
    instalacao: 'Orion · Controladora (SP)',
    versao: '2025.12.1',
    data: '2026-02-13T11:00:00',
    status: 'Programado',
    origem: 'Direto',
    batchId: null,
  },
];

export const novidades: Novidade[] = [
  {
    id: 'NOTE-001',
    version: '2025.12.1',
    category: 'fiscal',
    module: 'FS1 · Scritta SQL',
    title: 'Atualização da tabela TIPI',
    summary: 'Nova tabela TIPI publicada conforme decreto 12.345/2024, com revalidação automática das NCMs já cadastradas.',
    type: 'Adaptativa legal',
    PO: 'Thiago Dias',
    link: 'https://nasajon.github.io/news/pan/',
    imageUrl: '',
  },
  {
    id: 'NOTE-002',
    version: '2025.12.1',
    category: 'tesouraria_financas',
    module: 'TF2 · Tesouraria',
    title: 'Conciliação bancária com leitura de OFX em lote',
    summary: 'Importação de vários extratos OFX numa única operação, com relatório de divergências por conta antes de confirmar.',
    type: 'Nova funcionalidade',
    PO: 'Renata Coelho',
    link: 'https://nasajon.github.io/news/pan/',
    imageUrl: '',
  },
  {
    id: 'NOTE-003',
    version: '2025.12.0',
    category: 'departamento_pessoal',
    module: 'DP4 · Folha',
    title: 'Correção no cálculo de férias proporcionais',
    summary: 'Ajuste no arredondamento de avos para admissões no dia 31, que gerava diferença de um dia no provisionamento.',
    type: 'Correctiva',
    PO: 'Marcos Vinícius',
    link: 'https://nasajon.github.io/news/pan/',
    imageUrl: '',
  },
  {
    id: 'NOTE-004',
    version: '2025.12.0',
    category: 'contabilidade',
    module: 'CT1 · Contábil',
    title: 'Fechamento contábil até 4x mais rápido',
    summary: 'Reescrita da apuração de saldos por competência; balancetes de empresas com mais de 2 milhões de lançamentos saem em segundos.',
    type: 'Evolução técnica',
    PO: 'Ana Prado',
    link: 'https://nasajon.github.io/news/pan/',
    imageUrl: '',
  },
  {
    id: 'NOTE-005',
    version: '2025.11.2',
    category: 'suprimentos',
    module: 'SP3 · Compras',
    title: 'Cotação com comparativo lado a lado',
    summary: 'A tela de cotação passa a comparar até cinco fornecedores na mesma visão, destacando prazo e condição de pagamento.',
    type: 'Nova funcionalidade',
    PO: 'Renata Coelho',
    link: 'https://nasajon.github.io/news/pan/',
    imageUrl: '',
  },
  {
    id: 'NOTE-006',
    version: '2025.11.2',
    category: 'recursos_humanos',
    module: 'RH2 · eSocial',
    title: 'eSocial: novo leiaute do evento S-2200',
    summary: 'Adequação ao leiaute S-1.3 do eSocial, com migração automática dos eventos pendentes de transmissão.',
    type: 'Adaptativa legal',
    PO: 'Marcos Vinícius',
    link: 'https://nasajon.github.io/news/pan/',
    imageUrl: '',
  },
];

/** Catálogo de versões por instalação (o backend real calcula isto por elegibilidade). */
export const versoesPorInstalacao: Record<string, Array<{ id: string; nome: string }>> = {
  'ins-ORION-CTRL': [
    { id: '2025.12.1', nome: '2025.12.1 · estável recomendada' },
    { id: '2025.12.0', nome: '2025.12.0' },
    { id: '2025.11.3', nome: '2025.11.3' },
  ],
  'ins-DFM-PROD': [
    { id: '2025.12.1', nome: '2025.12.1 · estável recomendada' },
    { id: '2025.11.3', nome: '2025.11.3' },
    { id: '2025.11.2', nome: '2025.11.2' },
  ],
  'ins-VENTO-PROD': [
    { id: '2025.12.1', nome: '2025.12.1 · estável recomendada' },
    { id: '2025.12.0', nome: '2025.12.0' },
    { id: '2025.10.1', nome: '2025.10.1' },
  ],
  // ACME já está na versão mais recente — a lista vazia é resposta válida.
  'ins-ACME-HOMOLOG': [],
};
